// /reset-password — pantalla donde aterriza el usuario tras hacer click
// en el link del email de recovery. El link contiene tokens en URL hash
// que Supabase parsea automáticamente vía onAuthStateChange — el
// AuthProvider los recoge y queda un user "recovery session" activo.
//
// El formulario pide nueva password + confirmación. Submit dispara
// supabase.auth.updateUser({ password }). Post-success el usuario
// queda logueado (Supabase mantiene la sesión); navegamos a / y dejamos
// que el rol-resolver del Login lo redirija al home correcto.
//
// Sub-decisiones Sprint 2 B3:
//   - Mantener al usuario logueado post-update (standard pattern). No
//     forzamos re-login con la nueva password.
//   - Si el user llega a /reset-password sin recovery session activa
//     (link expirado, URL pegada a mano, manipulada), mostramos error
//     + link "Pedir un link nuevo" → /forgot-password.

import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/app/components/auth/AuthProvider";
import { AuthLayout } from "@/app/components/auth/AuthLayout";
import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";

type Mode = "checking-session" | "idle" | "submitting" | "no-session";

export function ResetPassword() {
  const navigate = useNavigate();
  const { status } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [mode, setMode] = useState<Mode>("checking-session");
  const [error, setError] = useState<string | null>(null);

  // Esperamos a que AuthProvider termine de procesar el token del URL
  // hash (onAuthStateChange dispara recovery). Si tras settling no hay
  // sesión, mostramos error en lugar de form.
  useEffect(() => {
    if (status === "loading") return;
    if (status === "authenticated") {
      setMode("idle");
    } else {
      setMode("no-session");
    }
  }, [status]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError("La contraseña tiene que tener al menos 8 caracteres");
      return;
    }
    if (!/[0-9!@#$%^&*(),.?":{}|<>]/.test(password)) {
      setError("La contraseña tiene que incluir al menos 1 número o símbolo");
      return;
    }
    if (password !== confirm) {
      setError("Las contraseñas no coinciden");
      return;
    }

    setMode("submitting");
    const { error: updateError } = await supabase.auth.updateUser({
      password,
    });

    if (updateError) {
      setError(updateError.message);
      setMode("idle");
      return;
    }

    // Usuario logueado con nueva password. Navegamos a / y dejamos
    // que el flow productivo lo redirija a su home (kine o paciente).
    navigate("/");
  };

  if (mode === "checking-session") {
    return (
      <AuthLayout title="Cargando…">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AuthLayout>
    );
  }

  if (mode === "no-session") {
    return (
      <AuthLayout
        title="Link inválido o expirado"
        subtitle="El link de recuperación que usaste ya no es válido."
        footer={
          <Link
            to="/login"
            className="text-primary hover:underline font-medium"
          >
            Volver al login
          </Link>
        }
      >
        <p className="text-sm text-muted-foreground mb-4">
          Los links de recuperación son válidos por 24 horas y solo se pueden
          usar una vez. Pedí uno nuevo si necesitás.
        </p>
        <Link to="/forgot-password" className="block">
          <Button type="button" className="w-full">
            Pedir un link nuevo
          </Button>
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Nueva contraseña"
      subtitle="Elegí una contraseña nueva para tu cuenta."
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="password"
            className="block text-sm font-medium mb-1.5"
          >
            Nueva contraseña
          </label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Mínimo 8 caracteres + 1 número o símbolo"
              disabled={mode === "submitting"}
              aria-invalid={!!error}
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
              aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
              tabIndex={-1}
            >
              {showPassword ? (
                <EyeOff className="w-4 h-4" />
              ) : (
                <Eye className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>

        <div>
          <label
            htmlFor="confirm"
            className="block text-sm font-medium mb-1.5"
          >
            Confirmar contraseña
          </label>
          <Input
            id="confirm"
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Repetí la contraseña"
            disabled={mode === "submitting"}
            aria-invalid={!!error}
          />
        </div>

        {error && (
          <div
            role="alert"
            className="text-sm rounded-md px-3 py-2 bg-red-50 text-red-900 border border-red-200"
          >
            {error}
          </div>
        )}

        <Button
          type="submit"
          className="w-full"
          disabled={mode === "submitting"}
        >
          {mode === "submitting" ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Guardando…
            </>
          ) : (
            "Guardar contraseña"
          )}
        </Button>
      </form>
    </AuthLayout>
  );
}
